import { type JSX, useState } from 'react';
import { Box, Button, Stack, TextField } from '@mui/material';

export interface LicenseLookupProps {
  onSubmit: (licenseId: string) => void;
  busy?: boolean;
  /** Optional initial value (e.g., from a query string). */
  initialValue?: string;
}

/**
 * License-ID lookup form. Single text input + submit. Auto-focuses on
 * mount; submits on Enter. The parent owns the actual fetch — this
 * component only collects input.
 */
export function LicenseLookup({ onSubmit, busy, initialValue = '' }: LicenseLookupProps): JSX.Element {
  const [value, setValue] = useState(initialValue);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim().length === 0) return;
        onSubmit(value.trim());
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }}>
        <TextField
          label="License ID"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="00000000-0000-4000-8000-000000000000"
          autoFocus
          fullWidth
          inputProps={{ autoComplete: 'off', spellCheck: false, 'aria-label': 'License ID', 'data-testid': 'lookup-input' }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={busy === true || value.trim().length === 0}
          data-testid="lookup-submit"
          sx={{ py: 1, minWidth: 120 }}
        >
          {busy === true ? 'Looking up…' : 'Look up'}
        </Button>
      </Stack>
    </Box>
  );
}
